import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Library,
  Layers,
  Sparkles,
  Trash2,
  Plus,
  Pencil,
  X,
  Save,
} from "lucide-react";
import { MobileDialog } from "../components/mobile";

const API = process.env.REACT_APP_BACKEND_URL;

const AUDIENCE_OPTIONS = [
  { v: "both", l: "Both (Learner + PDI)" },
  { v: "learner", l: "Learner only" },
  { v: "pdi", l: "PDI only" },
];

const ANIMATION_HINTS = [
  "roundabout_mini_right",
  "roundabout_single_ahead",
  "roundabout_two_lane_right",
  "junction_t_left",
  "junction_t_right",
  "junction_crossroads_unmarked",
  "parking_forward_bay",
  "parking_reverse_bay",
  "parking_parallel",
];

/**
 * AdminContentLibrary — full CRUD for Categories + Scenarios.
 *  - List/filter
 *  - Seed MVP / Phase 2
 *  - New / Edit modals (reusable, schema-aware)
 *  - Delete with confirm
 */
export default function AdminContentLibrary() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [tab, setTab] = useState("scenarios");
  const [cats, setCats] = useState([]);
  const [sc, setSc] = useState([]);
  const [comps, setComps] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [seeding, setSeeding] = useState(false);

  // Modal state
  const [editingCat, setEditingCat] = useState(null); // {} for new, {…} for edit
  const [editingSc, setEditingSc] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [c, s, k] = await Promise.all([
        axios.get(`${API}/api/admin/content/categories`, { withCredentials: true }),
        axios.get(`${API}/api/admin/content/scenarios`, { withCredentials: true }),
        axios.get(`${API}/api/admin/content/competencies`, { withCredentials: true }),
      ]);
      setCats(c.data.items || []);
      setSc(s.data.items || []);
      setComps(k.data.items || []);
    } catch (error) {
      setLoadError(error.response?.status === 403 ? "This session does not have access to the library." : "The library could not be loaded.");
      toast.error("Couldn't load library");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const seed = async (kind) => {
    setSeeding(true);
    try {
      const path = kind === "phase2" ? "seed-phase-2" : "seed-mvp";
      const { data } = await axios.post(`${API}/api/admin/content/${path}`, {}, { withCredentials: true });
      toast.success(`Seeded ${data.inserted_categories} categories · ${data.inserted_scenarios} scenarios`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const removeScenario = async (id) => {
    if (!window.confirm("Delete this scenario? This is permanent.")) return;
    try {
      await axios.delete(`${API}/api/admin/content/scenarios/${id}`, { withCredentials: true });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  const removeCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await axios.delete(`${API}/api/admin/content/categories/${id}`, { withCredentials: true });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  const filtered = filterCat ? sc.filter((s) => s.category_key === filterCat) : sc;

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-24">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="cl-back" onClick={() => goBack()} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold text-slate-900 dark:text-white">Driving Content Library</h1>
            <p className="text-[11px] text-slate-500">Categories, scenarios, animations, coaching scripts.</p>
          </div>
          <button
            data-testid="cl-seed-mvp"
            onClick={() => seed("mvp")}
            disabled={seeding || loading || !!loadError}
            className="px-3 py-2 text-xs font-extrabold rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60 inline-flex items-center gap-1"
          >
            {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Seed MVP
          </button>
          <button
            data-testid="cl-seed-phase2"
            onClick={() => seed("phase2")}
            disabled={seeding || loading || !!loadError}
            className="px-3 py-2 text-xs font-extrabold rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60 inline-flex items-center gap-1"
          >
            <Sparkles className="w-3.5 h-3.5" /> Phase 2
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="flex gap-1">
            {[
              ["scenarios", "Scenarios", Library],
              ["categories", "Categories", Layers],
            ].map(([k, l, Icon]) => (
              <button
                key={k}
                data-testid={`cl-tab-${k}`}
                onClick={() => setTab(k)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-extrabold rounded-full border ${
                  tab === k
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700"
                }`}
              ><Icon className="w-3.5 h-3.5" /> {l}</button>
            ))}
          </div>
          <button
            data-testid={tab === "categories" ? "cl-new-category" : "cl-new-scenario"}
            disabled={loading || !!loadError}
            onClick={() => tab === "categories" ? setEditingCat({}) : setEditingSc({})}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-extrabold rounded-full bg-violet-600 text-white hover:bg-violet-500"
          >
            <Plus className="w-3.5 h-3.5" /> New {tab === "categories" ? "category" : "scenario"}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : loadError ? (
          <div role="alert"><p>{loadError} Contents are unavailable.</p><button onClick={load}>Retry</button></div>
        ) : tab === "categories" ? (
          <CategoryList cats={cats} onDelete={removeCategory} onEdit={setEditingCat} />
        ) : (
          <>
            <select
              data-testid="cl-filter-category"
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              className="w-full mb-3 px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-white"
            >
              <option value="">All categories</option>
              {cats.map((c) => <option key={c.key} value={c.key}>{c.title}</option>)}
            </select>
            <ScenarioList items={filtered} onDelete={removeScenario} onEdit={setEditingSc} />
          </>
        )}
      </div>

      {editingCat !== null && (
        <CategoryModal
          initial={editingCat}
          onClose={() => setEditingCat(null)}
          onSaved={() => { setEditingCat(null); load(); }}
        />
      )}
      {editingSc !== null && (
        <ScenarioModal
          initial={editingSc}
          cats={cats}
          comps={comps}
          onClose={() => setEditingSc(null)}
          onSaved={() => { setEditingSc(null); load(); }}
        />
      )}
    </div>
  );
}

function CategoryList({ cats, onDelete, onEdit }) {
  if (cats.length === 0) return <Empty label="No categories yet — tap Seed MVP or New category." />;
  return (
    <ul data-testid="cl-categories" className="space-y-2">
      {cats.map((c) => (
        <li key={c.id} className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-slate-900 dark:text-white">{c.title}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{c.description}</p>
              <div className="flex gap-1 mt-1 flex-wrap">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">{c.key}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">{c.audience}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">sort {c.sort_order}</span>
                {!c.active && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">inactive</span>}
              </div>
            </div>
            <button data-testid={`cl-cat-edit-${c.key}`} onClick={() => onEdit(c)} className="p-2 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg">
              <Pencil className="w-4 h-4" />
            </button>
            <button data-testid={`cl-cat-delete-${c.key}`} onClick={() => onDelete(c.id)} className="p-2 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function ScenarioList({ items, onDelete, onEdit }) {
  if (items.length === 0) return <Empty label="No scenarios — Seed MVP for Roundabouts/Junctions/Parking or tap New scenario." />;
  return (
    <ul data-testid="cl-scenarios" className="space-y-2">
      {items.map((s) => (
        <li key={s.id} className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-slate-900 dark:text-white truncate">{s.title}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{s.summary}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">{s.category_key}/{s.key}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">L{s.progression_level || 1}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">{s.audience}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">{(s.competencies || []).length} comp</span>
                {s.animation_key && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">{s.animation_key}</span>}
              </div>
            </div>
            <button data-testid={`cl-sc-edit-${s.key}`} onClick={() => onEdit(s)} className="p-2 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg">
              <Pencil className="w-4 h-4" />
            </button>
            <button data-testid={`cl-sc-delete-${s.key}`} onClick={() => onDelete(s.id)} className="p-2 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Empty({ label }) {
  return (
    <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-dashed border-slate-200 dark:border-slate-700 p-6 text-center">
      <Library className="w-5 h-5 mx-auto text-slate-400 mb-1" />
      <p className="text-xs text-slate-500 font-bold">{label}</p>
    </div>
  );
}

// ─────── Modals ───────

function ModalShell({ title, onClose, children, testid }) {
  return (
    <MobileDialog
      bareContent
      open
      onClose={onClose}
      testid={testid}
    >
      <div className="bg-white dark:bg-slate-900 w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="flex-1 text-sm font-extrabold text-slate-900 dark:text-white">{title}</h2>
          <button data-testid="cl-modal-close" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
      </div>
    </MobileDialog>
  );
}

function CategoryModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    key: initial?.key || "",
    title: initial?.title || "",
    description: initial?.description || "",
    audience: initial?.audience || "both",
    sort_order: initial?.sort_order ?? 100,
    active: initial?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.key || !form.title) {
      toast.error("Key and title are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/api/admin/content/categories/${initial.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/content/categories`, form, { withCredentials: true });
      }
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      testid="cl-category-modal"
      title={isEdit ? `Edit category — ${initial.title}` : "New category"}
      onClose={onClose}
    >
      <div className="space-y-3">
        <Field label="Key (stable, lowercase)">
          <input
            data-testid="cl-cat-key"
            disabled={isEdit}
            value={form.key}
            onChange={(e) => set("key", e.target.value.toLowerCase().replace(/\s+/g, "_"))}
            placeholder="e.g. roundabouts"
            className={fieldCls(isEdit)}
          />
        </Field>
        <Field label="Title">
          <input data-testid="cl-cat-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={fieldCls()} />
        </Field>
        <Field label="Description">
          <textarea data-testid="cl-cat-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className={fieldCls()} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Audience">
            <select data-testid="cl-cat-audience" value={form.audience} onChange={(e) => set("audience", e.target.value)} className={fieldCls()}>
              {AUDIENCE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
          <Field label="Sort order">
            <input data-testid="cl-cat-sort" type="number" value={form.sort_order} onChange={(e) => set("sort_order", parseInt(e.target.value || "100", 10))} className={fieldCls()} />
          </Field>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
          <input data-testid="cl-cat-active" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} />
          Active (visible to learners and PDIs)
        </label>
      </div>

      <div className="sticky bottom-0 -mx-4 mt-4 px-4 py-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 flex gap-2">
        <button onClick={onClose} className="flex-1 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-extrabold text-sm">Cancel</button>
        <button data-testid="cl-cat-save" onClick={submit} disabled={saving} className="flex-1 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-extrabold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isEdit ? "Save changes" : "Create category"}
        </button>
      </div>
    </ModalShell>
  );
}

function ScenarioModal({ initial, cats, comps, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    category_key: initial?.category_key || cats[0]?.key || "",
    key: initial?.key || "",
    title: initial?.title || "",
    summary: initial?.summary || "",
    audience: initial?.audience || "both",
    animation_key: initial?.animation_key || "",
    lane_position_notes: initial?.lane_position_notes || "",
    give_way_points: initial?.give_way_points || [],
    hazard_cues: initial?.hazard_cues || [],
    learner_prompts: initial?.learner_prompts || [],
    questions_to_ask: initial?.questions_to_ask || [],
    reflection_questions: initial?.reflection_questions || [],
    safety_notes: initial?.safety_notes || [],
    audio_script: initial?.audio_script || "",
    pdi_coaching_phrases: initial?.pdi_coaching_phrases || [],
    pdi_lesson_structure: initial?.pdi_lesson_structure || [],
    pdi_risk_opportunities: initial?.pdi_risk_opportunities || [],
    competencies: initial?.competencies || [],
    progression_level: initial?.progression_level ?? 1,
    sort_order: initial?.sort_order ?? 100,
    active: initial?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const compsByLevel = useMemo(() => {
    const out = { 1: [], 2: [], 3: [], 4: [] };
    for (const c of comps) out[c.level || 1].push(c);
    return out;
  }, [comps]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setList = (k, v) => set(k, v.split("\n").map((s) => s.trim()).filter(Boolean));
  const toggleComp = (key) => {
    const has = form.competencies.includes(key);
    set("competencies", has ? form.competencies.filter((c) => c !== key) : [...form.competencies, key]);
  };

  const submit = async () => {
    if (!form.category_key || !form.key || !form.title) {
      toast.error("Category, key and title are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/api/admin/content/scenarios/${initial.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/content/scenarios`, form, { withCredentials: true });
      }
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      testid="cl-scenario-modal"
      title={isEdit ? `Edit scenario — ${initial.title}` : "New scenario"}
      onClose={onClose}
    >
      <div className="space-y-4">
        <Section title="Identity">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select data-testid="cl-sc-category" disabled={isEdit} value={form.category_key} onChange={(e) => set("category_key", e.target.value)} className={fieldCls(isEdit)}>
                {cats.map((c) => <option key={c.key} value={c.key}>{c.title}</option>)}
              </select>
            </Field>
            <Field label="Key">
              <input data-testid="cl-sc-key" disabled={isEdit} value={form.key} onChange={(e) => set("key", e.target.value.toLowerCase().replace(/\s+/g, "_"))} placeholder="e.g. mini_roundabout" className={fieldCls(isEdit)} />
            </Field>
          </div>
          <Field label="Title">
            <input data-testid="cl-sc-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={fieldCls()} />
          </Field>
          <Field label="Summary (1-2 lines)">
            <textarea data-testid="cl-sc-summary" rows={2} value={form.summary} onChange={(e) => set("summary", e.target.value)} className={fieldCls()} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Audience">
              <select data-testid="cl-sc-audience" value={form.audience} onChange={(e) => set("audience", e.target.value)} className={fieldCls()}>
                {AUDIENCE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </Field>
            <Field label="Progression L1-L4">
              <select data-testid="cl-sc-level" value={form.progression_level} onChange={(e) => set("progression_level", parseInt(e.target.value, 10))} className={fieldCls()}>
                {[1, 2, 3, 4].map((l) => <option key={l} value={l}>L{l}</option>)}
              </select>
            </Field>
            <Field label="Sort order">
              <input data-testid="cl-sc-sort" type="number" value={form.sort_order} onChange={(e) => set("sort_order", parseInt(e.target.value || "100", 10))} className={fieldCls()} />
            </Field>
          </div>
          <Field label="Animation key (frontend SVG)">
            <input data-testid="cl-sc-animation" list="cl-anim-hints" value={form.animation_key} onChange={(e) => set("animation_key", e.target.value)} placeholder="e.g. roundabout_mini_right" className={fieldCls()} />
            <datalist id="cl-anim-hints">
              {ANIMATION_HINTS.map((h) => <option key={h} value={h} />)}
            </datalist>
          </Field>
          <label className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
            <input data-testid="cl-sc-active" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} />
            Active (published)
          </label>
        </Section>

        <Section title="Learner-facing content">
          <Field label="Lane position notes">
            <textarea data-testid="cl-sc-lane" rows={2} value={form.lane_position_notes} onChange={(e) => set("lane_position_notes", e.target.value)} className={fieldCls()} />
          </Field>
          <ListField label="Give-way points (one per line)" testid="cl-sc-giveway" value={form.give_way_points} onChange={(v) => setList("give_way_points", v)} />
          <ListField label="Hazard cues (one per line)" testid="cl-sc-hazards" value={form.hazard_cues} onChange={(v) => setList("hazard_cues", v)} />
          <ListField label="Learner prompts (one per line)" testid="cl-sc-prompts" value={form.learner_prompts} onChange={(v) => setList("learner_prompts", v)} />
          <ListField label="Questions to ask the instructor (one per line)" testid="cl-sc-questions" value={form.questions_to_ask} onChange={(v) => setList("questions_to_ask", v)} />
          <ListField label="Reflection questions (one per line)" testid="cl-sc-reflection" value={form.reflection_questions} onChange={(v) => setList("reflection_questions", v)} />
          <ListField label="Safety notes (one per line)" testid="cl-sc-safety" value={form.safety_notes} onChange={(v) => setList("safety_notes", v)} />
          <Field label="Audio script (single paragraph)">
            <textarea data-testid="cl-sc-audio" rows={3} value={form.audio_script} onChange={(e) => set("audio_script", e.target.value)} className={fieldCls()} />
          </Field>
        </Section>

        <Section title="PDI lesson builder">
          <ListField label="Coaching phrases (one per line)" testid="cl-sc-coaching" value={form.pdi_coaching_phrases} onChange={(v) => setList("pdi_coaching_phrases", v)} />
          <ListField label="Lesson structure steps (one per line)" testid="cl-sc-structure" value={form.pdi_lesson_structure} onChange={(v) => setList("pdi_lesson_structure", v)} />
          <ListField label="Risk-management opportunities (one per line)" testid="cl-sc-risk" value={form.pdi_risk_opportunities} onChange={(v) => setList("pdi_risk_opportunities", v)} />
        </Section>

        <Section title={`DVSA competencies (${form.competencies.length}/14)`}>
          <div data-testid="cl-sc-competencies" className="space-y-2">
            {[1, 2, 3, 4].map((lvl) => (
              <div key={lvl}>
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mb-1">Level {lvl}</p>
                <div className="flex flex-wrap gap-1.5">
                  {compsByLevel[lvl].map((c) => {
                    const on = form.competencies.includes(c.key);
                    return (
                      <button
                        key={c.key}
                        data-testid={`cl-sc-comp-${c.key}`}
                        type="button"
                        onClick={() => toggleComp(c.key)}
                        className={`text-[11px] font-bold px-2 py-1 rounded-full border ${
                          on
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700"
                        }`}
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className="sticky bottom-0 -mx-4 mt-4 px-4 py-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 flex gap-2">
        <button onClick={onClose} className="flex-1 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-extrabold text-sm">Cancel</button>
        <button data-testid="cl-sc-save" onClick={submit} disabled={saving} className="flex-1 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-extrabold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isEdit ? "Save changes" : "Create scenario"}
        </button>
      </div>
    </ModalShell>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 mb-2">{title}</p>
      <div className="space-y-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold text-slate-600 dark:text-slate-300">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ListField({ label, value, onChange, testid }) {
  return (
    <Field label={label}>
      <textarea
        data-testid={testid}
        rows={Math.min(8, Math.max(3, (value || []).length + 1))}
        value={(value || []).join("\n")}
        onChange={(e) => onChange(e.target.value)}
        className={fieldCls()}
        placeholder="One per line"
      />
    </Field>
  );
}

function fieldCls(disabled) {
  return `w-full px-3 py-2 text-sm rounded-xl border bg-white dark:bg-slate-900 dark:text-white ${
    disabled ? "border-slate-200 dark:border-slate-800 opacity-60" : "border-slate-200 dark:border-slate-700"
  }`;
}
